/**
 * Filtre de periode « Reçu par le serveur ».
 *
 * Il porte sur l'instant ou le **serveur** a recu la derniere version
 * (`updated_at`), pas sur la date de l'evenement : celle-ci vit dans une charge
 * utile non indexable. Les deux bornes sont des jours civils locaux, inclusifs.
 *
 * L'API n'accepte que des instants ATOM **a la seconde** : `toISOString()`
 * ajoute des millisecondes, que le serveur refuse en 422. D'ou cette fonction.
 */

const two = (value: number): string => String(value).padStart(2, '0');

/** `2026-10-01T00:00:00+02:00` : instant local, decalage compris, sans fraction. */
export const atomSeconds = (date: Date): string => {
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);

  return (
    `${String(date.getFullYear()).padStart(4, '0')}-${two(date.getMonth() + 1)}-${two(date.getDate())}` +
    `T${two(date.getHours())}:${two(date.getMinutes())}:${two(date.getSeconds())}` +
    `${sign}${two(Math.floor(abs / 60))}:${two(abs % 60)}`
  );
};

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `AAAA-MM-JJ` en jour local reel ; `null` pour une saisie vide ou un jour inexistant (31 fevrier). */
const parseDay = (value: string): { y: number; m: number; d: number } | null => {
  const match = DAY.exec(value);

  if (match === null) {
    return null;
  }

  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(y, m - 1, d);

  return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d ? { y, m, d } : null;
};

/** Premiere seconde du jour local, ou `undefined` si la saisie n'est pas un jour. */
export const startOfDayAtom = (value: string): string | undefined => {
  const day = parseDay(value);

  return day === null ? undefined : atomSeconds(new Date(day.y, day.m - 1, day.d, 0, 0, 0));
};

/** Derniere seconde du jour local (borne haute inclusive). */
export const endOfDayAtom = (value: string): string | undefined => {
  const day = parseDay(value);

  return day === null ? undefined : atomSeconds(new Date(day.y, day.m - 1, day.d, 23, 59, 59));
};

/** Message d'une periode refusee avant l'envoi, ou `null`. Le serveur refuse `from > to` en 422. */
export const periodError = (from: string, to: string): string | null => {
  if (from !== '' && parseDay(from) === null) {
    return 'Le début de la période n’est pas une date valide.';
  }

  if (to !== '' && parseDay(to) === null) {
    return 'La fin de la période n’est pas une date valide.';
  }

  if (from !== '' && to !== '' && from > to) {
    return 'Le début de la période est postérieur à sa fin.';
  }

  return null;
};
